import {
  Component,
  OnInit,
  input,
  output,
  inject,
  signal,
  computed,
  effect,
  DestroyRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs/operators';
import { lastValueFrom, Observable } from 'rxjs';

// Angular Material Imports
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ScrollingModule } from '@angular/cdk/scrolling';

// Custom Components
import {
  CustomStepperComponent,
  StepperSelectionEvent,
} from '../custom-stepper/custom-stepper.component';
import { CustomStepComponent } from '../custom-stepper/custom-step.component';
import { AddNhhConsentComponent } from '../add-nhh-consent.component';
import { AddHhConsentComponent } from '../add-hh-consent.component';
import {
  SmartMeterAgreementComponent,
  SmartMeterAgreementData,
} from '../smart-meter-agreement/smart-meter-agreement.component';
import { SmartConsentOptionsComponent } from '../smart-consent-options/smart-consent-options.component';
import {
  CustomRadioButtonComponent,
  CustomRadioButtonOption,
} from '../custom-radio-button/custom-radio-button.component';

// Services
import { ConsentService } from '@services/consent.service';
import { LoginService } from '@services/login.service';
import { MeterService } from '@services/meter.service';
import { UtilService } from '@services/util.service';
import { LogService } from '@services/log.service';
import { VerificationAdapterService } from '@components/meter-details/verification-dialog/verification-adapter.service';

// Models
import {
  MeterType,
  MeterDetails,
  Meter,
  ConsentStatus,
} from '@models/meter.model';
import {
  Consent,
  ConsentType,
  RelationshipType,
  ConsentRequest,
  NotificationStatus,
  ConsentCategory,
  SmartConsentValue,
} from '@models/consent.model';
import { Partner } from '@models/partner.model';
import {
  VerificationResponse,
  VerificationStatus,
} from '@components/meter-details/verification-dialog/verification-dialog.component';
import { GenericPopupComponent } from '@components/shared/generic-popup.component';
import {
  getConsentExpiryRange,
  mapConsentRequestMeterToMeterDetails,
  meterDetailsToMeter,
} from '@utils/meter.utils';
import { PreferencesService } from '@services/preferences.service';

interface PrefilledValues {
  relationshipType?: RelationshipType;
  scope?: 'ANNUAL' | 'HH' | 'MONTHLY';
  expiryDate?: Date;
}

interface StepConfig {
  id: string;
  title: string;
  component: string;
  isVisible: () => boolean;
  isValid: () => boolean;
}

@Component({
  selector: 'app-handle-consent-simplified',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatProgressBarModule,
    ScrollingModule,
    CustomStepperComponent,
    CustomStepComponent,
    AddNhhConsentComponent,
    AddHhConsentComponent,
    SmartMeterAgreementComponent,
    SmartConsentOptionsComponent,
    CustomRadioButtonComponent,
  ],
  templateUrl: './handle-consent-simplified.component.html',
  styleUrls: ['./handle-consent-simplified.component.scss'],
})
export class HandleConsentSimplifiedComponent implements OnInit {
  readonly preferences = inject(PreferencesService).getPreferences();
  readonly supportedMeterTypes = this.preferences.supportedMeterTypes;
  readonly supportedSmartMeterConsentTypes =
    this.preferences.supportedSmartMeterConsentTypes;

  // Signal input for consent request ID
  readonly consentRequestId = input<string | null>(null);

  // Output event for completion
  readonly complete = output<{
    status: 'SUCCESS' | 'FAILURE';
    message: string;
  }>();
  readonly shareIncomplete = output<{
    requestId: string | null;
    consentRequest: ConsentRequest;
    meters: Meter[];
    hasSkippedMeters: boolean;
  }>();

  @ViewChild('stepper') stepper!: CustomStepperComponent;
  @ViewChild('stepperContainer') stepperContainer!: ElementRef<HTMLDivElement>;

  // Injected services
  private readonly destroyRef = inject(DestroyRef);
  private readonly consentService = inject(ConsentService);
  private readonly loginService = inject(LoginService);
  private readonly meterService = inject(MeterService);
  private readonly utilService = inject(UtilService);
  private readonly logService = inject(LogService);
  private readonly verificationAdapter = inject(VerificationAdapterService);

  // Verification popup dialog reference
  private verificationDialogRef: MatDialogRef<GenericPopupComponent> | null =
    null;

  // Step management
  readonly currentStepIndex = signal(0);

  // State signals
  readonly isLoading = signal(false);
  readonly isValidating = signal(false);
  readonly isSharing = signal(false);
  readonly validationSuccess = signal(false);
  readonly validationError = signal('');
  readonly consentReference = signal('');
  readonly requestingOrganization = signal<Partner | null>(null);
  readonly allMetersAlreadyAdded = signal(false);
  readonly currentConsentRequest = signal<ConsentRequest | null>(null);
  readonly currentRequestId = signal<string | null>(null);
  readonly meterDetailsError = signal(false);
  readonly hasSkippedMeters = signal(false);

  // Data signals
  readonly availableMeters = signal<MeterDetails[]>([]);
  readonly allRequestedMeters = signal<MeterDetails[]>([]);
  readonly relationshipType = signal<RelationshipType | null>(null);
  readonly granularity = signal<'ANNUAL' | 'HH' | null>(null);
  readonly expiryDate = signal<Date>(
    new Date(new Date().setFullYear(new Date().getFullYear() + 3)),
  );
  readonly consentDecision = signal('');

  // Prefilled values tracking
  readonly hasPrefilledRelationship = signal(false);
  readonly hasPrefilledGranularity = signal(false);
  readonly hasPrefilledExpiryDate = signal(false);

  // Form controls
  readonly smartMeterAgreementControl =
    new FormControl<SmartMeterAgreementData | null>(null, Validators.required);
  readonly consentDataControl = new FormControl<string | File | any | null>(
    null,
  );
  readonly consentControl = new FormControl<Consent | null>(
    null,
    Validators.required,
  );

  // Signals mirroring form control values for reactive computed dependencies
  readonly consentValue = toSignal(
    this.consentControl.valueChanges.pipe(startWith(this.consentControl.value)),
    { initialValue: this.consentControl.value },
  );

  readonly consentValueStatus = toSignal(
    this.consentControl.statusChanges.pipe(map((status) => status === 'VALID')),
    { initialValue: this.consentControl.valid },
  );

  readonly consentDataValue = toSignal(
    this.consentDataControl.valueChanges.pipe(
      startWith(this.consentDataControl.value),
    ),
    { initialValue: this.consentDataControl.value },
  );

  // Form control signals
  private readonly smartMeterAgreementValid = toSignal(
    this.smartMeterAgreementControl.statusChanges.pipe(
      map((status) => status === 'VALID'),
    ),
    { initialValue: this.smartMeterAgreementControl.valid },
  );

  // Constants
  private readonly expiryRange = getConsentExpiryRange();
  readonly minExpiryDate = this.expiryRange.minExpiryDate;
  readonly maxExpiryDate = this.expiryRange.maxExpiryDate;
  readonly relationshipOptions = this.loginService.getRelationshipOptions();

  // Granularity options
  readonly granularityOptions: CustomRadioButtonOption[] = [
    {
      label: 'Annual (or) Monthly',
      value: 'ANNUAL',
      description: 'Access meter data at annual or monthly intervals',
    },
    {
      label: 'Half-hourly',
      value: 'HH',
      description: 'Access detailed half-hourly meter data',
    },
  ];

  // Virtual scroll configuration
  readonly meterCardItemSize = 106;
  readonly shouldUseVirtualScrollingForCards = computed(() => {
    return false; // Temporarily disable virtual scrolling for cards due to UI issues

    const count = this.availableMeters().length;
    return count > 3; // Use virtual scrolling for cards only when more than 3 items
  });

  readonly meterCardViewportHeight = computed(() => {
    const count = this.availableMeters().length;
    const visible = Math.min(count, 3);
    return visible * this.meterCardItemSize + 8; // +8 for padding
  });

  readonly meterTableRowHeight = 36;
  readonly shouldUseVirtualScrolling = computed(() => {
    return false; // Temporarily disable virtual scrolling for table due to UI issues

    const count = this.allMetersAlreadyAdded()
      ? this.allRequestedMeters().length
      : this.availableMeters().length;
    return count > 5; // Use virtual scrolling only for more than 5 items
  });

  readonly meterTableViewportHeight = computed(() => {
    const count = this.allMetersAlreadyAdded()
      ? this.allRequestedMeters().length
      : this.availableMeters().length;
    const rows = Math.min(count, 10); // +1 for header row
    return rows * this.meterTableRowHeight;
  });

  // Computed properties
  readonly isSmartMeter = computed(() => {
    const meters = this.availableMeters();
    return meters.every((meter) => meter.meterType === MeterType.SMART);
  });

  readonly computedConsentCategory = computed((): ConsentCategory => {
    const meters = this.availableMeters();
    if (!meters || meters.length === 0) return ConsentCategory.CHECKBOX;

    const granularity = this.granularity();

    const smartMeters = meters.filter((m) => m.meterType === MeterType.SMART);
    let isSmartMeterConsent = false;
    if (meters.length === smartMeters.length && smartMeters.length > 0) {
      const firstAddress = smartMeters[0].addressId;
      const sameAddress = smartMeters.every(
        (meter) => meter.addressId === firstAddress,
      );
      if (sameAddress) {
        isSmartMeterConsent = true;
      }
    }

    if (isSmartMeterConsent) {
      if (granularity === 'HH') {
        return ConsentCategory.SMART_METER;
      } else {
        return ConsentCategory.CHECKBOX;
      }
    } else {
      if (granularity === 'HH') {
        return ConsentCategory.LOA;
      }
      if (granularity === 'ANNUAL') {
        return ConsentCategory.CHECKBOX;
      }
    }

    const nHHMeters = meters.filter(
      (m) => m.meterType === MeterType.NON_HALF_HOURLY,
    );

    if (meters.length === nHHMeters.length) {
      return ConsentCategory.CHECKBOX; // All selected meters are Non Half Hourly
    } else if (meters.length === smartMeters.length) {
      return ConsentCategory.CHECKBOX; // All selected meters are Smart to be considered as non-HH
    } else {
      return ConsentCategory.LOA; // At least one meter is Half Hourly
    }
  });

  readonly hasRequestingOrg = computed(() => {
    const org = this.requestingOrganization();
    return org !== null && org !== undefined;
  });

  // Step configuration
  readonly stepConfigs = computed((): StepConfig[] => [
    {
      id: 'meters',
      title: 'Confirm Meters',
      component: 'meters',
      isVisible: () => true, // Always show first step
      isValid: computed(() => {
        // Can't proceed if still loading
        if (this.isLoading()) return false;

        // Can't proceed if there's a meter details error
        if (this.meterDetailsError()) return false;

        // If all meters are already added, that's valid
        if (this.allMetersAlreadyAdded()) return true;

        // Otherwise, proceed if we have any meters
        return this.availableMeters().length > 0;
      }),
    },
    {
      id: 'relationship',
      title: 'Relationship Type',
      component: 'relationship',
      isVisible: () =>
        !this.allMetersAlreadyAdded() && !this.hasPrefilledRelationship(),
      isValid: computed(() => this.relationshipType() !== null),
    },
    {
      id: 'expiry',
      title: 'Consent Expiry',
      component: 'expiry',
      isVisible: () =>
        !this.allMetersAlreadyAdded() && !this.hasPrefilledExpiryDate(),
      isValid: computed(() => {
        const expiry = this.expiryDate();
        return expiry >= this.minExpiryDate && expiry <= this.maxExpiryDate;
      }),
    },
    {
      id: 'granularity',
      title: 'Data Granularity',
      component: 'granularity',
      isVisible: () =>
        !this.allMetersAlreadyAdded() &&
        this.isSmartMeter() &&
        !this.hasPrefilledGranularity(),
      isValid: computed(() => this.granularity() !== null),
    },
    {
      id: 'smartMeterAgreement',
      title: 'Smart Meter Agreement',
      component: 'smartMeterAgreement',
      isVisible: () =>
        !this.allMetersAlreadyAdded() &&
        this.computedConsentCategory() === 'SMART_METER' &&
        this.granularity() === 'HH',
      isValid: this.smartMeterAgreementValid,
    },
    {
      id: 'addConsent',
      title: 'Add Consent',
      component: 'addConsent',
      isVisible: () => !this.allMetersAlreadyAdded(),
      isValid: computed(() => {
        if (this.validationSuccess()) return true;
        const consentCategory = this.computedConsentCategory();
        const consent = this.consentValue();
        const consentData = this.consentDataValue();
        switch (consentCategory) {
          case ConsentCategory.SMART_METER:
            return consentData !== null && consentData !== undefined;
          default:
            return consent !== null && consent !== undefined;
        }
      }),
    },
    {
      id: 'shareConsent',
      title: 'Share Consent',
      component: 'shareConsent',
      isVisible: this.hasRequestingOrg,
      isValid: computed(() => this.consentDecision() !== ''),
    },
    {
      id: 'confirmation',
      title: 'Confirmation',
      component: 'confirmation',
      isVisible: this.hasRequestingOrg,
      isValid: computed(() => true),
    },
  ]);

  // Current step
  readonly currentStep = computed(() => {
    const index = this.currentStepIndex();
    const configs = this.stepConfigs();
    if (index >= 0 && index < configs.length) {
      return configs[index];
    }
    return null;
  });

  readonly isShareConsentStep = computed(
    () => this.currentStep()?.id === 'shareConsent',
  );
  readonly isAddConsentStep = computed(
    () => this.currentStep()?.id === 'addConsent',
  );
  readonly isConfirmationStep = computed(
    () => this.currentStep()?.id === 'confirmation',
  );

  // Navigation state
  readonly isFirstStep = computed(() => {
    const configs = this.stepConfigs();
    const currentIndex = this.currentStepIndex();

    for (let i = 0; i < currentIndex; i++) {
      if (configs[i].isVisible()) return false; // A visible step exists before this one
    }
    return true;
  });

  readonly isLastStep = computed(() => {
    const configs = this.stepConfigs();
    const currentIndex = this.currentStepIndex();

    for (let i = currentIndex + 1; i < configs.length; i++) {
      if (configs[i].isVisible()) return false; // A visible step exists after this one
    }
    return true;
  });

  readonly canProceed = computed(() => this.currentStep()?.isValid() || false);

  readonly nextButtonLabel = computed(() => {
    if (this.isAddConsentStep()) {
      return 'Add Consent';
    }
    return 'Next';
  });

  readonly isNextButtonLoading = computed(() => {
    return this.isValidating();
  });

  // Loading state computed property
  readonly isMetersLoading = computed(() => this.isLoading());

  // Error state computed property
  readonly hasCriticalMeterError = computed(() => this.meterDetailsError());

  // Track meter function
  trackMeter = (_: number, meter: any) => meter?.meterNo;

  constructor() {
    // Setup form effects
    this.setupFormEffects();

    // Setup consent request monitoring
    this.setupConsentRequestMonitoring();
  }

  ngOnInit(): void {
    // Monitor consentRequestId changes
    // const requestId = this.consentRequestId();
    // if (requestId) {
    //   this.isLoading.set(true);
    //   this.handleConsentRequest(requestId);
    // } else {
    //   this.isLoading.set(false);
    // }
  }

  private setupConsentRequestMonitoring(): void {
    // Use effect to monitor consentRequestId input changes
    effect(() => {
      const requestId = this.consentRequestId();
      if (requestId) {
        this.isLoading.set(true);
        this.handleConsentRequest(requestId);
      } else {
        this.isLoading.set(false);
      }
    });
  }

  private setupFormEffects(): void {
    // Auto-create consent object when consent data changes
    effect(() => {
      const data = this.consentDataControl.value;
      const granularity = this.granularity();

      if (data && granularity) {
        try {
          const consent = this.createConsentObject(data);
          this.consentControl.setValue(consent);
        } catch (error) {
          console.error('Error creating consent object:', error);
          this.consentControl.setValue(null);
        }
      } else {
        this.consentControl.setValue(null);
      }
    });

    // Auto-proceed when validation succeeds on Add Consent step
    effect(() => {
      const validationSuccess = this.validationSuccess();
      const currentStep = this.currentStep();

      if (validationSuccess && currentStep?.id === 'addConsent') {
        setTimeout(() => {
          this.nextStep();
        }, 1000);
      }
    });

    effect(() => {
      const consentStatus = this.consentValueStatus();
      if (consentStatus && this.isAddConsentStep()) {
        const consentValue = this.consentControl.value;
        if (consentValue?.consentCategory !== ConsentCategory.SMART_METER) {
          this.autoAdvance();
        }
      }
    });
  }

  scrollToEnd(): void {
    const container = this.stepperContainer.nativeElement;

    if (!container) return;

    // Use setTimeout to ensure DOM has been updated before scrolling
    setTimeout(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }, 300);
  }

  private async handleConsentRequest(consentRequestId: string): Promise<void> {
    try {
      // Reset error states
      this.meterDetailsError.set(false);

      const consentRequest = await this.consentService
        .getConsentRequestById(consentRequestId)
        .toPromise();
      if (!consentRequest) {
        console.error('Consent request not found');
        return;
      }

      this.currentConsentRequest.set(consentRequest);
      let requestingOrg: Partner | null = null;

      if (consentRequest.agentOrg) {
        requestingOrg = await lastValueFrom(
          this.getSoftwareForOrg(consentRequest.agentOrg),
        );
      }

      this.requestingOrganization.set(requestingOrg);

      const requestedMeterNumbers =
        this.consentService.getAllMetersFromRequest(consentRequest);
      if (requestedMeterNumbers.length === 0) {
        console.warn('No meters found in consent request');
        return;
      }

      const existingMeters =
        (await this.meterService.getMeters(true).toPromise()) || [];
      const existingMeterNumbers = existingMeters.map((m) => m.meterNumber);
      const newMeterNumbers = requestedMeterNumbers.filter(
        (meterNumber) => !existingMeterNumbers.includes(meterNumber),
      );

      if (requestedMeterNumbers.length !== newMeterNumbers.length) {
        this.hasSkippedMeters.set(true);
      } else {
        this.hasSkippedMeters.set(false);
      }

      if (newMeterNumbers.length === 0) {
        this.allRequestedMeters.set(
          consentRequest.meters
            .map((m) => existingMeters.find((em) => em.meterNumber === m.mpxn))
            .filter((meter): meter is Meter => meter !== undefined)
            .map(
              (meter) =>
                ({
                  meterNo: meter.meterNumber,
                  meterType: meter.meterType,
                  fuelType: meter.fuelType,
                  addressAsLine: meter.address,
                  postCode: meter.postCode || '',
                  addressId: '',
                  currentSupplierName: meter.consumption
                    ? 'Unknown Supplier'
                    : '',
                  postTown: '',
                }) as MeterDetails,
            ),
        );
        this.allMetersAlreadyAdded.set(true);
        this.availableMeters.set([]);
        this.jumpToStep('shareConsent');
        return;
      } else {
        this.allRequestedMeters.set(
          consentRequest.meters.map(mapConsentRequestMeterToMeterDetails),
        );
      }

      this.allMetersAlreadyAdded.set(false);

      if (newMeterNumbers.length === 1) {
        const meterNumber = newMeterNumbers[0];
        const meterFromRequest = consentRequest.meters.find(
          (m) => m.mpxn === meterNumber,
        );

        if (meterFromRequest) {
          try {
            const details = await this.meterService
              .getMeterDetailsByMeterNo(meterNumber, meterFromRequest.fuelType)
              .toPromise();
            if (details && details.length > 0) {
              this.availableMeters.set(details);
            } else {
              this.setMeterMappings(consentRequest, newMeterNumbers);
            }
          } catch (error) {
            console.error(
              `Failed to fetch details for meter ${meterNumber}, using fallback.`,
              error,
            );
            this.meterDetailsError.set(true);
            this.setMeterMappings(consentRequest, newMeterNumbers);
          }
        } else {
          this.setMeterMappings(consentRequest, newMeterNumbers);
        }
      } else {
        this.setMeterMappings(consentRequest, newMeterNumbers);
      }

      const prefilledValues: PrefilledValues =
        consentRequest.customConfig?.consentDetails || {};
      this.applyPrefilledValues(prefilledValues);
    } catch (error) {
      console.error('Error handling consent request:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  private setMeterMappings(
    consentRequest: ConsentRequest,
    newMeterNumbers: string[],
  ): void {
    const availableMeters = consentRequest.meters
      .filter((m) => newMeterNumbers.includes(m.mpxn))
      .map(mapConsentRequestMeterToMeterDetails);

    this.availableMeters.set(availableMeters);
  }

  private applyPrefilledValues(prefilledValues: PrefilledValues): void {
    // Unsupported values (e.g. legacy 'occupier_and_landlord') are ignored so the customer is asked
    if (
      prefilledValues.relationshipType &&
      Object.values(RelationshipType).includes(prefilledValues.relationshipType)
    ) {
      this.relationshipType.set(prefilledValues.relationshipType);
      this.hasPrefilledRelationship.set(true);
    }

    if (
      prefilledValues.scope &&
      ['ANNUAL', 'HH', 'MONTHLY'].includes(prefilledValues.scope)
    ) {
      const granularityValue =
        prefilledValues.scope === 'MONTHLY' ? 'ANNUAL' : prefilledValues.scope;
      this.granularity.set(granularityValue as 'ANNUAL' | 'HH');
      this.hasPrefilledGranularity.set(true);
    }

    if (prefilledValues.expiryDate) {
      try {
        const expiryDate =
          prefilledValues.expiryDate instanceof Date
            ? prefilledValues.expiryDate
            : new Date(prefilledValues.expiryDate);

        if (
          !isNaN(expiryDate.getTime()) &&
          expiryDate >= this.minExpiryDate &&
          expiryDate <= this.maxExpiryDate
        ) {
          this.expiryDate.set(expiryDate);
          this.hasPrefilledExpiryDate.set(true);
        }
      } catch (error) {
        console.warn(
          'Error parsing prefilled expiry date:',
          prefilledValues.expiryDate,
          error,
        );
      }
    }
  }

  private createConsentObject(raw: SmartConsentValue): Consent {
    const scope = this.granularity();
    if (!scope)
      throw new Error('Scope must be selected before creating consent');

    return {
      consentCategory: raw.consentCategory,
      consentType: raw.consentType,
      consentData: raw.consentData,
      scope: scope,
    };
  }

  // Navigation methods
  nextStep(): void {
    const currentStep = this.currentStep();

    if (currentStep?.id === 'addConsent' && !this.validationSuccess()) {
      this.addConsent();
      return;
    }

    if (!this.canProceed()) return;

    const configs = this.stepConfigs();
    const currentIndex = this.currentStepIndex();

    let nextIndex = currentIndex + 1;
    while (nextIndex < configs.length) {
      if (configs[nextIndex].isVisible()) {
        // Log navigation to next step
        if (currentStep) {
          const currentStepNum = currentIndex + 1;
          const nextStepNum = nextIndex + 1;
          const nextStepTitle = configs[nextIndex].title;
          this.logService.addConsentActionLog(
            `CURRENT STEP:: ${currentStepNum}_${currentStep.title}, NEXT STEP:: ${nextStepNum}_${nextStepTitle}`,
          );
        }
        this.stepper.goToStep(nextIndex);
        return;
      }
      nextIndex++;
    }

    // If no next step, close the flow. Scenario where share is not applicable
    const consentRequest = this.currentConsentRequest();
    const relationshipType = this.relationshipType();
    if (consentRequest && !this.hasRequestingOrg()) {
      this.shareIncomplete.emit({
        consentRequest,
        meters: this.availableMeters().map((meter) =>
          meterDetailsToMeter(meter, relationshipType!),
        ),
        requestId: this.currentRequestId(),
        hasSkippedMeters: this.hasSkippedMeters(),
      });
    }
  }

  previousStep(): void {
    const currentStep = this.currentStep();
    const configs = this.stepConfigs();
    const currentIndex = this.currentStepIndex();

    let prevIndex = currentIndex - 1;
    while (prevIndex >= 0) {
      if (configs[prevIndex].isVisible()) {
        // Log navigation to previous step
        if (currentStep) {
          const currentStepNum = currentIndex + 1;
          const prevStepNum = prevIndex + 1;
          const prevStepTitle = configs[prevIndex].title;
          this.logService.addConsentActionLog(
            `CURRENT STEP:: ${currentStepNum}_${currentStep.title}, PREVIOUS STEP:: ${prevStepNum}_${prevStepTitle}`,
          );
        }
        this.stepper.goToStep(prevIndex);
        return;
      }
      prevIndex--;
    }
  }

  private jumpToStep(stepId: string): void {
    const configs = this.stepConfigs();
    const index = configs.findIndex((step) => step.id === stepId);
    if (index !== -1 && configs[index].isVisible()) {
      this.stepper.goToStep(index);
    }
  }

  private autoAdvance() {
    setTimeout(() => {
      this.nextStep();
    }, 500);
  }

  // Step-specific methods
  onRelationshipSelected(relationship: RelationshipType): void {
    this.relationshipType.set(relationship);

    // Auto-advance to next step
    this.autoAdvance();
  }

  onGranularitySelected(granularity: 'ANNUAL' | 'HH'): void {
    this.granularity.set(granularity);
    // Reset consent controls when granularity changes
    this.consentControl.reset();
    this.consentDataControl.reset();

    // Auto-advance to next step
    this.autoAdvance();
  }

  onConsentDecisionMade(decision: string): void {
    this.consentDecision.set(decision);

    // Auto-advance to next step
    this.autoAdvance();
  }

  // Wrapper methods for consent changes with logging
  onConsentChange(value: Consent | null): void {
    const consentCategory = this.computedConsentCategory();
    if (value) {
      this.logService.addConsentActionLog('CONSENT_PROVIDED', {
        consentCategory,
        consentType: value.consentType,
        scope: value.scope,
      });
    }
    this.consentControl.setValue(value);

    // Clear validation error when user changes consent type
    if (this.validationError()) {
      this.validationError.set('');
    }
  }

  onConsentDataChange(value: any): void {
    const consentCategory = this.computedConsentCategory();
    if (value) {
      this.logService.addConsentActionLog('SMART_CONSENT_DATA_PROVIDED', {
        consentCategory,
        consentType: value.consentType,
      });
    }
    this.consentDataControl.setValue(value);

    // Clear validation error when user changes consent type
    if (this.validationError()) {
      this.validationError.set('');
    }
  }

  async addConsent(): Promise<void> {
    if (!this.canProceed()) return;

    this.isValidating.set(true);
    this.validationError.set('');
    this.validationSuccess.set(false);

    try {
      const consentCategory = this.computedConsentCategory();
      if (consentCategory === ConsentCategory.SMART_METER) {
        //SMART METER CONSENT
        // Additional validation for MAC Address consent
        if (!this.consentDataControl.value) {
          const errorMessage = 'Please provide the MAC Address.';
          this.validationError.set(errorMessage);
          this.logService.addErrorLog('VALIDATION_ERROR', {
            message: errorMessage,
            context: 'addConsent',
          });
          return;
        }
        this.consentControl.setValue({
          consentCategory: ConsentCategory.SMART_METER,
          consentType: this.consentDataControl.value?.consentType,
          consentData: this.consentDataControl.value?.consentData,
          scope: this.granularity() as 'HH' | 'ANNUAL',
          moveInDate:
            this.smartMeterAgreementControl.value?.moveInDate || new Date(),
          termsAccepted:
            this.smartMeterAgreementControl.value?.termsAccepted || true,
        });
      }

      const {
        consent: consentData,
        email,
        relationshipType: relType,
      } = this.getFormData();

      if (!consentData) {
        const errorMessage = 'No consent data available for submission.';
        this.validationError.set(errorMessage);
        this.logService.addErrorLog('VALIDATION_ERROR', {
          message: errorMessage,
          context: 'addConsent',
        });
        this.isValidating.set(false);
        return;
      }

      const meterDetails: MeterDetails[] = this.availableMeters();
      const organisation = this.getOrganisationDetails();
      const startDate = new Date(new Date().setDate(new Date().getDate() + 1));
      const expiryDate = this.expiryDate();

      this.meterService
        .addNewMeter(
          consentData,
          meterDetails,
          email,
          relType,
          organisation,
          startDate,
          expiryDate,
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => this.handleConsentSubmissionSuccess(response),
          error: (error) => {
            this.handleConsentSubmissionError(
              error,
              'Failed to process your request. Please try again.',
            );
          },
          complete: () => {
            // This will be called on completion, but we handle loading state
            // in success/error handlers based on verification polling.
          },
        });
    } catch (error) {
      console.error('Error preparing to add consent:', error);
      const errorMessage =
        'Failed to initiate consent process. Please try again.';
      this.validationError.set(errorMessage);
      this.logService.addErrorLog('VALIDATION_ERROR', {
        message: errorMessage,
        context: 'addConsent',
        error,
      });
      this.isValidating.set(false);
    }
  }

  private handleConsentSubmissionSuccess(response: any): void {
    const requestId =
      response?.data?.requestId || response?.requestId || 'sdk-consent-request';
    this.startVerificationPolling(requestId);
  }

  private handleConsentSubmissionError(
    error: any,
    customErrorMessage?: string,
  ): void {
    let errorMessage = 'Please check your input and try again.';
    if (customErrorMessage) {
      errorMessage = customErrorMessage;
    }
    console.error('Error submitting consent:', error);
    const validationErrorMessage = `${error?.error?.message || error?.message}. ${errorMessage}`;
    this.validationError.set(validationErrorMessage);
    this.logService.addErrorLog('VALIDATION_ERROR', {
      message: errorMessage,
      context: 'handleConsentSubmissionError',
      error,
    });
    this.validationSuccess.set(false);
    this.isValidating.set(false);
    this.scrollToEnd();
  }

  private startVerificationPolling(requestId: string): void {
    // Open verification popup (disableClose=true to prevent closing by backdrop click)
    this.verificationDialogRef = this.utilService.openCloseablePopup(
      'Verifying Consent',
      'Please wait while we verify your consent. It might take up to 2 minutes.',
      true,
    );

    const pollFunction = (
      this.verificationAdapter as any
    ).createConsentPollingFunction(requestId);
    setTimeout(() => {
      this.pollVerificationStatus(pollFunction);
    }, 2000);
  }

  private pollVerificationStatus(pollFunction: () => any): void {
    pollFunction()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: VerificationResponse) => {
          this.handleVerificationResponse(response, pollFunction);
        },
        error: (error: any) => {
          console.error('Error during verification polling:', error);
          const errorMessage = 'Error during verification. Please try again.';
          this.validationError.set(errorMessage);
          this.logService.addErrorLog('VALIDATION_ERROR', {
            message: errorMessage,
            context: 'pollVerificationStatus',
            error,
          });
          this.validationSuccess.set(false);
          this.isValidating.set(false);
          this.closeVerificationPopup();
        },
      });
  }

  private closeVerificationPopup(): void {
    if (this.verificationDialogRef) {
      this.verificationDialogRef.close();
      this.verificationDialogRef = null;
    }
  }

  private handleVerificationResponse(
    response: VerificationResponse,
    pollFunction: () => any,
  ): void {
    switch (response.status) {
      case VerificationStatus.SUCCESS:
        this.validationSuccess.set(true);
        this.validationError.set('');
        this.isValidating.set(false);
        this.closeVerificationPopup();
        this.scrollToEnd();
        break;

      case VerificationStatus.ERROR:
      case VerificationStatus.FAILURE:
        this.validationSuccess.set(false);
        let failureMessage =
          response.message || 'Verification failed. Please try again.';

        // Append smart meter alternative method suggestion
        const consent = this.consentValue();
        if (consent?.consentType) {
          failureMessage = this.getCustomErrorMessage(consent.consentType);
        }

        if (consent?.consentCategory === ConsentCategory.SMART_METER) {
          const alternativeMethod =
            consent.consentType === 'MAC_ADDRESS'
              ? 'Card verification'
              : 'MAC Address';
          failureMessage += ` If this error persists, try ${alternativeMethod}.`;
        }

        this.validationError.set(failureMessage);
        this.logService.addErrorLog('VALIDATION_ERROR', {
          message: failureMessage,
          context: 'handleVerificationResponse',
          status: response.status,
        });
        this.isValidating.set(false);
        this.closeVerificationPopup();
        this.scrollToEnd();
        break;

      case VerificationStatus.PENDING:
        if (response.finalStatus) {
          this.validationSuccess.set(false);
          const timeoutMessage =
            response.message || 'Verification timed out. Please try again.';
          this.validationError.set(timeoutMessage);
          this.logService.addErrorLog('VALIDATION_ERROR', {
            message: timeoutMessage,
            context: 'handleVerificationResponse',
            status: response.status,
          });
          this.isValidating.set(false);
          this.closeVerificationPopup();
          this.scrollToEnd();
        } else {
          setTimeout(() => {
            this.pollVerificationStatus(pollFunction);
          }, 3000);
        }
        break;

      default:
        setTimeout(() => {
          this.pollVerificationStatus(pollFunction);
        }, 3000);
        break;
    }
  }

  async makeConsentDecision(decision: 'approve' | 'decline'): Promise<void> {
    if (!this.currentConsentRequest()) return;
    const consentRequestId = this.currentConsentRequest()!.consentRequestId;

    this.isSharing.set(true);

    try {
      if (decision === 'approve') {
        const shareId = await this.shareWithPartner();
        this.onConsentDecisionMade('approved');
        this.onComplete({ status: 'SUCCESS', message: consentRequestId });
      } else {
        // Use the ConsentService updateNotificationStatus method to decline
        await this.consentService
          .updateNotificationStatus(
            consentRequestId,
            NotificationStatus.DISMISSED,
          )
          .toPromise();
        this.onConsentDecisionMade('declined');
        this.onComplete({ status: 'FAILURE', message: 'REQUEST_DECLINED' });
      }

      this.nextStep(); // Go to confirmation
    } catch (error) {
      console.error('Error processing consent decision:', error);
      this.utilService.openSnackBar(
        'Failed to process consent decision',
        'Error',
        'error',
      );
    } finally {
      this.isSharing.set(false);
    }
  }

  private async shareWithPartner(): Promise<string> {
    const currentConsentRequest = this.currentConsentRequest();
    const selectedPartner = this.requestingOrganization();

    if (!currentConsentRequest || !selectedPartner) {
      throw new Error('Missing consent request data');
    }

    const metersToShare: Meter[] = this.allRequestedMeters().map((meter) => ({
      meterNumber: meter.meterNo,
      meterType: meter.meterType,
      fuelType: meter.fuelType,
      address: meter.addressAsLine,
      postCode: meter.postCode || 'Unknown',
      consentStatus: ConsentStatus.ACTIVE,
      startDate: new Date(),
      expiryDate: this.expiryDate(),
    }));

    const consentRequestId =
      currentConsentRequest.agentOrg === selectedPartner.name
        ? currentConsentRequest.consentRequestId
        : undefined;

    const shareId = await this.consentService
      .shareMeterWithPartner(
        metersToShare,
        selectedPartner,
        undefined,
        consentRequestId,
      )
      .toPromise();

    if (shareId) {
      this.consentReference.set(shareId);
      return shareId;
    } else {
      throw new Error('Failed to share meters - no share ID returned');
    }
  }

  private getCustomErrorMessage(consentType: ConsentType) {
    switch (consentType) {
      case ConsentType.MAC_ADDRESS:
        return 'MAC Address Verification failed. Please enter a valid MAC Address.';
      case ConsentType.STRIPE:
        return 'Card Verification failed. Please ensure your card\'s billing address matches with the address provided.';
      case ConsentType.LOA_UPLOAD:
        return 'LOA Validation failed. Please ensure your LOA document meets the requirements and try again.';
      case ConsentType.DIGITAL_SIGNATURE:
        return 'Digital Signature Verification failed. Please contact support.';
      default:
        return 'Please check your input and try again.';
    }
  }

  private getSoftwareForOrg(orgName: string): Observable<Partner | null> {
    return this.consentService.getAllOrganizations().pipe(
      map((organizations: Partner[]) => {
        const matchingOrg = organizations.find(
          (org: Partner) =>
            org.name?.trim().toLowerCase() === orgName.trim().toLowerCase(),
        );
        return matchingOrg || null;
      }),
    );
  }

  private getFormData() {
    return {
      expiryDate: this.expiryDate(),
      relationshipType: this.relationshipType() as RelationshipType,
      consent: this.consentControl.value as Consent | null,
      email: this.loginService.loginUser?.email || 'user@example.com',
      consentTimestamp: new Date(),
    };
  }

  private getOrganisationDetails() {
    return {
      name: this.loginService.loginUser?.companyName || 'Perse',
      role: this.loginService.loginUser?.role || 'Director',
      organisationNumber: this.loginService.loginUser?.companyId || '1234',
      organisationType: 'LTD',
    };
  }

  // Navigation event handlers
  onStepChange(event: StepperSelectionEvent): void {
    // Update any state when steps change if needed
    console.log(
      'Step changed from',
      event.previouslySelectedIndex,
      'to',
      event.selectedIndex,
    );
    this.currentStepIndex.set(event.selectedIndex);
  }

  setExpiryDate(date: Date): void {
    this.expiryDate.set(date);

    // Auto-advance to next step
    this.autoAdvance();
  }

  onSmartMeterTermsAgreed(event: SmartMeterAgreementData): void {
    this.smartMeterAgreementControl.setValue(event);
    this.scrollToEnd();
    // Auto-advance to next step
    this.autoAdvance();
  }

  readonly getCurrentStepValid = computed(() => {
    if (!this.stepper) return false;

    const currentStepConfig = this.currentStep();

    if (!currentStepConfig) return false;

    const isValid = currentStepConfig.isValid();
    console.log('Current Step:', currentStepConfig.id, 'isValid:', isValid);

    return isValid;
  });

  onComplete({
    status,
    message,
  }: {
    status: 'SUCCESS' | 'FAILURE';
    message: string;
  }): void {
    this.complete.emit({ status, message });
  }
}
